import React from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { createEmptySftpConnection } from '../dataHandlers/sftpSettingsHandlers';
import appConfig from '../../../config/appConfig';

const POLL_MIN = 15;
const POLL_MAX = 3600;

const ViewField = ({ label, value }) => (
  <Stack spacing={0.25}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
      {value || '—'}
    </Typography>
  </Stack>
);

const SftpConnectionDialog = ({
  open,
  onClose,
  onSubmit,
  isPending,
  mode = 'create',
  initialUsername = '',
  workerPublicKey = '',
  hasExistingCredentials = false,
  initialPollIntervalSeconds = 60,
  initialMaxConcurrentFiles = 2,
  onSwitchToEdit,
}) => {
  const [form, setForm] = React.useState(createEmptySftpConnection());

  const isEdit = mode === 'edit';
  const isView = mode === 'view';
  const passwordOptional = hasExistingCredentials;

  React.useEffect(() => {
    if (!open) return;

    setForm({
      ...createEmptySftpConnection(),
      sftp_username: initialUsername || '',
      poll_interval_seconds: String(initialPollIntervalSeconds ?? 60),
      max_concurrent_files: String(initialMaxConcurrentFiles ?? 2),
    });
  }, [open, initialUsername, initialPollIntervalSeconds, initialMaxConcurrentFiles]);

  const usernameError = !form.sftp_username.trim() ? 'SFTP username is required' : '';
  const passwordError = !passwordOptional && !form.sftp_password ? 'SFTP password is required for new connections' : '';
  const pollNumber = Number(form.poll_interval_seconds);
  const concurrentNumber = Number(form.max_concurrent_files);
  const pollValid = Number.isFinite(pollNumber) && pollNumber >= POLL_MIN && pollNumber <= POLL_MAX;
  const concurrentValid = Number.isFinite(concurrentNumber) && concurrentNumber >= 1;
  const pollError =
    form.poll_interval_seconds !== '' && !pollValid ? `Poll interval must be ${POLL_MIN}–${POLL_MAX}` : '';
  const concurrentError =
    form.max_concurrent_files !== '' && !concurrentValid ? 'Max concurrent files must be at least 1' : '';

  const canSave =
    form.sftp_username.trim() && (passwordOptional || form.sftp_password) && pollValid && concurrentValid && !isPending;

  const handleSubmit = () => {
    if (!canSave) return;

    onSubmit({
      username: form.sftp_username.trim(),
      password: form.sftp_password,
      poll_interval_seconds: pollNumber,
      max_concurrent_files: concurrentNumber,
    });
  };

  const dialogTitle = isView
    ? 'View SFTP configuration'
    : isEdit
      ? 'Edit SFTP configuration'
      : 'Create SFTP configuration';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{dialogTitle}</DialogTitle>
      <DialogContent dividers>
        {isView ? (
          <Stack spacing={2} sx={{ pt: 1 }}>
            <ViewField label="SFTP username" value={form.sftp_username} />
            <ViewField label="SFTP password" value="Stored securely (not shown)" />
            <ViewField label="Poll interval (seconds)" value={form.poll_interval_seconds} />
            <ViewField label="Max concurrent files" value={form.max_concurrent_files} />
            <ViewField
              label={`${appConfig.brandName} worker public key`}
              value={workerPublicKey || 'Not generated yet'}
            />
          </Stack>
        ) : (
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Client credentials are encrypted at rest. Password is never shown after save — leave blank when editing to
              keep the existing password. Poll settings control how often <code>sent_inbox</code> is scanned and how
              many files are processed in parallel.
            </Typography>

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  required
                  label="SFTP username"
                  value={form.sftp_username}
                  onChange={(e) => setForm({ ...form, sftp_username: e.target.value })}
                  error={Boolean(usernameError)}
                  helperText={usernameError || ' '}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  required={!passwordOptional}
                  type="password"
                  label="SFTP password"
                  value={form.sftp_password}
                  onChange={(e) => setForm({ ...form, sftp_password: e.target.value })}
                  error={Boolean(passwordError)}
                  helperText={
                    passwordError ||
                    (passwordOptional ? 'Leave blank to keep the current password' : 'Required for new connections')
                  }
                  autoComplete="new-password"
                />
              </Grid>
            </Grid>

            <Divider />

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  required
                  type="number"
                  label="Poll interval (seconds)"
                  value={form.poll_interval_seconds}
                  onChange={(e) => setForm({ ...form, poll_interval_seconds: e.target.value })}
                  inputProps={{ min: POLL_MIN, max: POLL_MAX }}
                  error={Boolean(pollError)}
                  helperText={pollError || ' '}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  required
                  type="number"
                  label="Max concurrent files"
                  value={form.max_concurrent_files}
                  onChange={(e) => setForm({ ...form, max_concurrent_files: e.target.value })}
                  inputProps={{ min: 1 }}
                  error={Boolean(concurrentError)}
                  helperText={concurrentError || ' '}
                />
              </Grid>
            </Grid>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {isView ? (
          <>
            <Button onClick={onClose}>Close</Button>
            {onSwitchToEdit && (
              <Button variant="contained" onClick={onSwitchToEdit}>
                Edit
              </Button>
            )}
          </>
        ) : (
          <>
            <Button onClick={onClose} disabled={isPending}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleSubmit} disabled={!canSave}>
              {isEdit ? 'Save changes' : 'Create configuration'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SftpConnectionDialog;
